import { Table, message } from "antd";
import _ from "lodash";
import { useState, useMemo, useCallback } from "react";
import { deleteIncident, getAllIncidents } from "../../helpers/api";
import { Badge, Modal } from "@mantine/core";
import smallTalk from "smalltalk";
import { useQuery } from "react-query";
import { format } from "date-fns";
import NewIncidentForm from "./components/new-incident";
import EditIncidentForm from "./components/edit-incident";
import { getUser } from "../../helpers/auth";
import { INCIDENT_TYPES, INCIDENT_STATUSES } from "../../helpers/config";
import { PageHeader } from "../../components";

const getStatusColor = (status) => {
  switch (status) {
    case "reported":
      return "blue";
    case "under_review":
      return "orange";
    case "resolved":
      return "green";
    case "closed":
      return "gray";
    default:
      return "gray";
  }
};

const getTypeColor = (type) => {
  return type === "clinical" ? "red" : "violet";
};

const IncidentsPage = () => {
  const user = getUser();
  const [filteredData, setFilteredData] = useState([]);
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
  });

  const {
    data: incidents,
    isFetching,
    refetch,
  } = useQuery({
    queryFn: () => getAllIncidents(user.branchId),
    queryKey: ["incidents", user.branchId],
    onSuccess: (data) => setFilteredData(data),
  });

  const handleReset = useCallback(() => {
    setFilteredData(incidents);
  }, [incidents]);

  const handleDelete = useCallback((id) => {
    smallTalk
      .confirm("Delete Incident", "This action is irreversible. Continue?", {
        buttons: {
          ok: "YES",
          cancel: "NO",
        },
      })
      .then(() => {
        message.loading("deleting...");
        deleteIncident(user.branchId, id).then((res) => {
          message.success("done!");
          refetch();
        });
      })
      .catch((ex) => message.error("no action taken"));
  }, [refetch, user.branchId]);

  const formatDateTime = useCallback((date, time) => {
    if (!date) return "-";
    try {
      const dateObj = new Date(date);
      const formattedDate = format(dateObj, "MMM d, yyyy");
      
      if (time) {
        const [hours, minutes] = time.split(":");
        const hour = parseInt(hours, 10);
        const min = parseInt(minutes, 10);
        const timeObj = new Date();
        timeObj.setHours(hour, min, 0);
        const formattedTime = format(timeObj, "h:mm a");
        
        return (
          <div>
            <div>{formattedDate}</div>
            <div className="text-muted small">{formattedTime}</div>
          </div>
        );
      }
      return formattedDate;
    } catch (e) {
      return "-";
    }
  }, []);

  const columns = useMemo(() => [
    {
      title: "Date & Time",
      width: 180,
      sorter: (a, b) =>
        new Date(a?.incidentDate) > new Date(b?.incidentDate),
      sortDirections: ["descend", "ascend"],
      render: (text, record) => (
        <span>{formatDateTime(record?.incidentDate, record?.incidentTime)}</span>
      ),
    },
    {
      title: "Type",
      width: 120,
      sorter: (a, b) => a?.incidentType > b?.incidentType,
      sortDirections: ["descend", "ascend"],
      render: (text, record) => (
        <Badge
          color={getTypeColor(record?.incidentType)}
          variant="light">
          {INCIDENT_TYPES.find((t) => t.value === record?.incidentType)?.label ||
            record?.incidentType}
        </Badge>
      ),
    },
    {
      title: "Staff",
      width: 150,
      render: (text, record) => (
        <span>
          {record?.staff
            ? `${record.staff.firstName} ${record.staff.lastName}`
            : "-"}
        </span>
      ),
    },
    {
      title: "Status",
      width: 130,
      sorter: (a, b) => a?.status > b?.status,
      sortDirections: ["descend", "ascend"],
      render: (text, record) => (
        <Badge color={getStatusColor(record?.status)} variant="light">
          {INCIDENT_STATUSES.find((s) => s.value === record?.status)?.label ||
            record?.status}
        </Badge>
      ),
    },
    {
      title: "Submitted On",
      width: 150,
      sorter: (a, b) =>
        new Date(a?.createdAt) > new Date(b?.createdAt),
      sortDirections: ["descend", "ascend"],
      render: (text, record) => {
        if (!record?.createdAt) return "-";
        try {
          const dateObj = new Date(record.createdAt);
          return <span>{format(dateObj, "MMM d, yyyy")}</span>;
        } catch (e) {
          return "-";
        }
      },
    },
    {
      title: "",
      width: 170,
      render: (text, record) => (
        <>
          <button
            className="button app-btn is-ghost me-2"
            onClick={() =>
              setModal({
                title: "View/Edit Incident",
                content: (
                  <EditIncidentForm onSuccess={refetch} id={record.id} />
                ),
                isOpen: true,
              })
            }>
            <span className="bi bi-eye me-1"></span>
            View
          </button>
          <button
            className="button app-btn is-light text-danger is-ghost"
            onClick={() => {
              handleDelete(record.id);
            }}>
            <span className="bi bi-trash"></span>
          </button>
        </>
      ),
    },
  ], [refetch, handleDelete, formatDateTime]);

  return (
    <>
      <PageHeader
        title="Incidents"
        description="Manage incident reports"
        hasBackButton={false}
      />
      <Modal
        onClose={() => setModal({ ...modal, isOpen: false })}
        opened={modal.isOpen}
        title={modal.title}
        size="lg"
        zIndex={50}>
        {modal.content}
      </Modal>
      <div className="d-flex justify-content-between">
        <div className="d-flex mb-3">
          <button
            className={`button app-btn btn-prim me-3`}
            onClick={() =>
              setModal({
                title: "New Incident Report",
                content: <NewIncidentForm onSuccess={refetch} />,
                isOpen: true,
              })
            }>
            <span className="bi bi-plus-circle me-2"></span>
            New Incident
          </button>
        </div>

        <div className="d-flex">
          <div>
            <Badge color="teal" variant="outline" size="lg" className="me-2">
              {filteredData?.length || 0}
            </Badge>
            Incidents
          </div>
          <button
            onClick={handleReset}
            className="button app-btn btn-text ms-2 d-flex align-items-center">
            <span className="bi bi-arrow-clockwise me-2"></span>
            <span className="d-none d-md-inline"> Reset filters</span>
          </button>
          <button className="button app-btn" onClick={() => refetch()}>
            <span className="bi bi-cycle"></span>
            <span className="d-none d-md-inline">Refresh</span>
          </button>
        </div>
      </div>

      <div className="row my-3">
        <div className="col-md-6 col-12 mb-3">
          <div className="field">
            <input
              className="input"
              onChange={(e) =>
                setFilteredData(
                  incidents?.filter(
                    (inc) =>
                      inc?.description
                        ?.toLowerCase()
                        .includes(e.target.value.toLowerCase()) ||
                      inc?.incidentType
                        ?.toLowerCase()
                        .includes(e.target.value.toLowerCase()) ||
                      (inc?.staff &&
                        `${inc.staff.firstName} ${inc.staff.lastName}`
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()))
                  )
                )
              }
              title="search here"
              placeholder="Search by description, type, or staff"
            />
          </div>
        </div>
        <div className="col-md-3 col-6 mb-3">
          <select
            className="input"
            onChange={(e) => {
              if (e.target.value === "") {
                setFilteredData(incidents);
              } else {
                setFilteredData(
                  incidents?.filter((inc) => inc.incidentType === e.target.value)
                );
              }
            }}>
            <option value="">All Types</option>
            {INCIDENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 col-6 mb-3">
          <select
            className="input"
            onChange={(e) => {
              if (e.target.value === "") {
                setFilteredData(incidents);
              } else {
                setFilteredData(
                  incidents?.filter((inc) => inc.status === e.target.value)
                );
              }
            }}>
            <option value="">All Statuses</option>
            {INCIDENT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Table
            columns={columns}
            className="border"
            dataSource={filteredData}
            rowKey={(record) => record.id}
            loading={isFetching}
          />
        </div>
      </div>
    </>
  );
};

export { IncidentsPage };
